import React from 'react';
import { createRoot } from 'react-dom/client';
import { format } from 'date-fns';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { getClientInvoiceClinicLogoDataUrl, getNutritionistInvoiceContext, type Invoice } from '@/lib/api/invoices';
import calorieScienceInvoiceLogo from '@/assets/images/menu-bar-logo.svg';
import { formatInvoiceCurrencyAmount } from '@/utils/invoiceCurrency';

export interface InvoicePdfLineItem {
  description: string;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
}

export interface InvoicePdfPayload {
  invoiceNo: string;
  currency: string;
  clinicLogoUrl?: string | null;
  fromName: string;
  fromEmail: string;
  billToName: string;
  billToEmail: string;
  issueDate: string;
  dueDate: string;
  paidAt?: string;
  lineItems: InvoicePdfLineItem[];
  subtotal: number;
  taxPct: number;
  taxAmount: number;
  discount: number;
  total: number;
  status?: string;
  notes?: string | null;
}

export interface InvoicePdfFallbackFrom {
  name?: string;
  email?: string;
  clinicLogoUrl?: string | null;
  showLogoOnInvoice?: boolean;
}

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};

const readFirstString = (...values: unknown[]): string => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
};

const readFirstBoolean = (...values: unknown[]): boolean | undefined => {
  for (const value of values) {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (normalized === 'true') return true;
      if (normalized === 'false') return false;
    }
  }
  return undefined;
};

const CLINIC_LOGO_KEYS = ['clinicLogoUrl', 'clinicLogo', 'practiceLogoUrl', 'practiceLogo', 'logoUrl'] as const;
const CLINIC_LOGO_NESTED_KEYS = [
  'nutritionistSnapshot',
  'nutritionist',
  'practiceInfo',
  'practice',
  'profile',
  'user',
] as const;

const readClinicLogoUrlFromRecord = (value: unknown, depth = 0): string => {
  const record = asRecord(value);
  const direct = readFirstString(...CLINIC_LOGO_KEYS.map((key) => record[key]));
  if (direct || depth >= 2) {
    return direct;
  }

  for (const key of CLINIC_LOGO_NESTED_KEYS) {
    const nested = readClinicLogoUrlFromRecord(record[key], depth + 1);
    if (nested) {
      return nested;
    }
  }

  return '';
};

const readShowLogoOnInvoiceFromRecord = (value: unknown, depth = 0): boolean | undefined => {
  const record = asRecord(value);
  const direct = readFirstBoolean(record.showLogoOnInvoice);
  if (typeof direct === 'boolean' || depth >= 2) {
    return direct;
  }

  for (const key of CLINIC_LOGO_NESTED_KEYS) {
    const nested = readShowLogoOnInvoiceFromRecord(record[key], depth + 1);
    if (typeof nested === 'boolean') {
      return nested;
    }
  }

  return undefined;
};

export const getInvoiceShowLogoOnInvoice = (
  invoice?: Invoice | null,
  fallbackShowLogoOnInvoice = true
): boolean => {
  return readShowLogoOnInvoiceFromRecord(invoice) ?? fallbackShowLogoOnInvoice;
};

export const getInvoiceClinicLogoUrl = (
  invoice?: Invoice | null,
  fallbackLogoUrl?: string | null,
  fallbackShowLogoOnInvoice = true
): string => {
  if (!getInvoiceShowLogoOnInvoice(invoice, fallbackShowLogoOnInvoice)) {
    return '';
  }
  return readFirstString(readClinicLogoUrlFromRecord(invoice), fallbackLogoUrl);
};

/** Build PDF payload from API invoice (e.g. after GET /client-invoices/:id). */
export function invoiceApiToPdfPayload(
  inv: Invoice,
  fallbackFrom?: InvoicePdfFallbackFrom
): InvoicePdfPayload {
  const client = (inv.clientSnapshot || {}) as Record<string, unknown>;
  const billToName =
    (typeof client.fullName === 'string' && client.fullName.trim()) ||
    (typeof client.name === 'string' && client.name.trim()) ||
    [client.firstName, client.lastName].filter(Boolean).join(' ').trim() ||
    '—';
  const billToEmail = (typeof client.email === 'string' && client.email.trim()) || '—';

  const nutritionist = (inv.nutritionistSnapshot || {}) as Record<string, unknown>;
  const fromName =
    (typeof nutritionist.name === 'string' && nutritionist.name.trim()) ||
    (typeof nutritionist.fullName === 'string' && nutritionist.fullName.trim()) ||
    fallbackFrom?.name ||
    '—';
  const fromEmail =
    (typeof nutritionist.email === 'string' && nutritionist.email.trim()) || fallbackFrom?.email || '—';

  const issueD = new Date(inv.issueDate || inv.createdAt);
  const dueD = new Date(inv.dueDate);
  const paidD = inv.paidAt ? new Date(inv.paidAt) : null;
  const issueDate = Number.isNaN(issueD.getTime()) ? '—' : format(issueD, 'd MMM yyyy');
  const dueDate = Number.isNaN(dueD.getTime()) ? '—' : format(dueD, 'd MMM yyyy');
  const paidAt = !paidD || Number.isNaN(paidD.getTime()) ? undefined : format(paidD, 'd MMM yyyy');

  const currency = (inv.currency || 'USD').toUpperCase();
  const lineItems = (inv.lineItems || []).map((li) => {
    const qty = Number(li.qty) || 0;
    const unitPrice = Number(li.unitPrice) || 0;
    const lineTotal = Number(li.total) || qty * unitPrice;
    return {
      description: li.description || '—',
      quantity: qty,
      unitPrice,
      lineTotal,
    };
  });

  const subtotal = Number(inv.subtotal) || lineItems.reduce((s, l) => s + l.lineTotal, 0);
  const taxPct = Number(inv.taxPct) || 0;
  const taxAmount = Number(inv.taxAmount) || subtotal * (taxPct / 100);
  const discount = Number(inv.discount) || 0;
  const total =
    Number(inv.totalDue) || Math.max(0, subtotal + taxAmount - discount);

  return {
    invoiceNo: inv.invoiceNo || inv.id || '—',
    currency,
    clinicLogoUrl: getInvoiceClinicLogoUrl(inv, fallbackFrom?.clinicLogoUrl, fallbackFrom?.showLogoOnInvoice ?? true) || null,
    fromName,
    fromEmail,
    billToName,
    billToEmail,
    issueDate,
    dueDate,
    paidAt,
    lineItems,
    subtotal,
    taxPct,
    taxAmount,
    discount,
    total,
    status: inv.status || '',
    notes: inv.notes ?? null,
  };
}

export async function getInvoicePdfFallbackFromProfile(
  fallbackFrom: InvoicePdfFallbackFrom = {}
): Promise<InvoicePdfFallbackFrom> {
  if (fallbackFrom.showLogoOnInvoice === false) {
    return { ...fallbackFrom, clinicLogoUrl: null };
  }

  if (fallbackFrom.clinicLogoUrl?.trim() && fallbackFrom.showLogoOnInvoice === true) {
    return fallbackFrom;
  }

  try {
    const context = await getNutritionistInvoiceContext();
    return {
      ...fallbackFrom,
      showLogoOnInvoice: context.showLogoOnInvoice,
      clinicLogoUrl: context.showLogoOnInvoice ? context.clinicLogoUrl || null : null,
    };
  } catch (error) {
    console.warn('Could not load clinic logo for invoice PDF fallback', error);
    return fallbackFrom;
  }
}

export async function withInvoiceClinicLogoDataUrl(
  invoiceId: string | null | undefined,
  payload: InvoicePdfPayload
): Promise<InvoicePdfPayload> {
  if (!invoiceId || !payload.clinicLogoUrl) {
    return payload;
  }

  try {
    const clinicLogoDataUrl = await getClientInvoiceClinicLogoDataUrl(invoiceId);
    return clinicLogoDataUrl ? { ...payload, clinicLogoUrl: clinicLogoDataUrl } : payload;
  } catch (error) {
    console.warn('Could not load invoice clinic logo data URL for PDF', error);
    return payload;
  }
}

const money = (currency: string, value: number): string => {
  return formatInvoiceCurrencyAmount(Math.max(0, value), currency);
};

const rateLabel = (value: number): string => {
  if (Number.isInteger(value)) return String(value);
  return value.toFixed(2).replace(/\.?0+$/, '');
};

/**
 * Card width matches `InvoiceDetailsDialog` content (~704px dialog − 2×20px padding).
 * Styles mirror `InvoiceDetailsDialog` / create-flow `step === 'invoice'` invoice card.
 */
const INVOICE_CARD_PX = 664;
const HERO_GRADIENT = 'linear-gradient(171deg, #20aedc 0%, #1a8fb8 60%, #0e6a8a 100%)';
const TABLE_GRID = '1.5fr 0.5fr 0.8fr 0.8fr' as const;

function InvoiceHtmlView({ payload }: { payload: InvoicePdfPayload }) {
  const c = payload.currency || 'USD';
  const clinicLogoSrc = payload.clinicLogoUrl?.trim();
  const statusLower = (payload.status || '').toLowerCase();
  const isPaid = statusLower === 'paid';
  const paymentDateLabel = isPaid ? 'Paid Date' : 'Due Date';
  const paymentDateValue = isPaid ? payload.paidAt || payload.dueDate : payload.dueDate;
  const totalLabel = isPaid ? 'Total Paid' : 'Total Due';

  const font = `"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, sans-serif`;

  const p = (base: React.CSSProperties): React.CSSProperties => ({
    margin: 0,
    boxSizing: 'border-box',
    fontFamily: font,
    ...base,
  });

  const cell: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: TABLE_GRID,
    alignItems: 'start',
    borderBottom: '1px solid #f9fafb',
    paddingTop: 13,
    paddingBottom: 13,
  };

  return (
    <div
      data-invoice-pdf-root
      style={{
        width: INVOICE_CARD_PX,
        boxSizing: 'border-box',
        backgroundColor: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: 14,
        overflow: 'hidden',
        fontFamily: font,
        color: '#1e2939',
        WebkitFontSmoothing: 'antialiased',
      }}
    >
      <div
        style={{
          minHeight: 78,
          boxSizing: 'border-box',
          padding: '18px 24px 14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 24,
        }}
      >
        <img
          src={calorieScienceInvoiceLogo}
          alt=""
          width={223}
          style={{
            display: 'block',
            width: 223,
            maxWidth: 223,
            maxHeight: 40,
            height: 'auto',
            objectFit: 'contain',
            objectPosition: 'left center',
            flexShrink: 0,
          }}
        />
        {clinicLogoSrc ? (
          <img
            src={clinicLogoSrc}
            alt=""
            width={240}
            style={{
              display: 'block',
              width: 'auto',
              maxWidth: 250,
              maxHeight: 60,
              height: 'auto',
              objectFit: 'contain',
              objectPosition: 'right center',
              marginLeft: 'auto',
            }}
          />
        ) : null}
      </div>

      <div
        style={{
          height: 70,
          boxSizing: 'border-box',
          padding: '12px 24px',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          backgroundImage: HERO_GRADIENT,
        }}
      >
        <div>
          <p style={p({ color: 'rgba(255,255,255,0.6)', fontSize: 12, lineHeight: '12px', textTransform: 'uppercase' })}>Invoice</p>
          <p style={p({ color: '#fff', fontWeight: 500, fontSize: 20, lineHeight: '20px', marginTop: 1 })}>{payload.invoiceNo}</p>
        </div>
      </div>

      <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
            gap: 24,
          }}
        >
          <div>
            <p style={p({ color: '#99a1af', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 })}>From</p>
            <p style={p({ color: '#1e2939', fontWeight: 600, fontSize: 14, marginTop: 4 })}>{payload.fromName || '—'}</p>
            <p
              style={p({
                color: '#6a7282',
                fontSize: 12,
                marginTop: 4,
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
              })}
            >
              {payload.fromEmail || '—'}
            </p>
          </div>
          <div>
            <p style={p({ color: '#99a1af', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 })}>Bill To</p>
            <p style={p({ color: '#1e2939', fontWeight: 600, fontSize: 14, marginTop: 4 })}>{payload.billToName || '—'}</p>
            <p
              style={p({
                color: '#6a7282',
                fontSize: 12,
                marginTop: 4,
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
              })}
            >
              {payload.billToEmail || '—'}
            </p>
          </div>
          <div>
            <p style={p({ color: '#99a1af', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 })}>Issue Date</p>
            <p style={p({ color: '#364153', fontSize: 14, marginTop: 6 })}>{payload.issueDate}</p>
          </div>
          <div>
            <p style={p({ color: '#99a1af', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 })}>{paymentDateLabel}</p>
            <p style={p({ color: '#364153', fontSize: 14, marginTop: 6 })}>{paymentDateValue}</p>
          </div>
        </div>

        <div style={{ borderTop: '1px solid #f3f4f6' }} />

        <div>
          <div
            style={{
              ...cell,
              borderBottom: '1px solid #f3f4f6',
              paddingTop: 0,
              paddingBottom: 8,
            }}
          >
            <span style={p({ color: '#99a1af', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 })}>Description</span>
            <span style={p({ color: '#99a1af', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, textAlign: 'center' })}>Qty</span>
            <span style={p({ color: '#99a1af', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, textAlign: 'right' })}>Unit Price</span>
            <span style={p({ color: '#99a1af', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, textAlign: 'right' })}>Total</span>
          </div>

          {payload.lineItems.map((line, idx) => (
            <div key={idx} style={cell}>
              <span style={p({ color: '#364153', fontSize: 14 })}>{line.description || '-'}</span>
              <span style={p({ color: '#4a5565', fontSize: 14, textAlign: 'center' })}>{line.quantity}</span>
              <span style={p({ color: '#4a5565', fontSize: 14, textAlign: 'right' })}>{money(c, line.unitPrice)}</span>
              <span style={p({ color: '#364153', fontWeight: 500, fontSize: 14, textAlign: 'right' })}>{money(c, line.lineTotal)}</span>
            </div>
          ))}
        </div>

        <div style={{ borderTop: '1px solid #eceff3', paddingTop: 12, paddingLeft: 4, paddingRight: 4 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
            <span style={p({ color: '#6a7282', fontSize: 14 })}>Subtotal</span>
            <span style={p({ color: '#6a7282', fontSize: 14 })}>{money(c, payload.subtotal)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
            <span style={p({ color: '#6a7282', fontSize: 14 })}>Tax ({rateLabel(payload.taxPct)}%)</span>
            <span style={p({ color: '#6a7282', fontSize: 14 })}>{money(c, payload.taxAmount)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={p({ color: '#6a7282', fontSize: 14 })}>Discount</span>
            <span style={p({ color: '#6a7282', fontSize: 14 })}>-{money(c, payload.discount)}</span>
          </div>
          <div style={{ borderTop: '1px solid #eceff3', paddingTop: 6, display: 'flex', justifyContent: 'space-between' }}>
            <span style={p({ color: '#1e2939', fontWeight: 700, fontSize: 16 })}>{totalLabel}</span>
            <span style={p({ color: '#20aedc', fontWeight: 700, fontSize: 20 })}>{money(c, payload.total)}</span>
          </div>
        </div>

        {payload.notes ? (
          <div
            style={{
              backgroundColor: 'rgba(239,246,255,0.6)',
              border: '1px solid #dbeafe',
              borderRadius: 14,
              padding: 16,
            }}
          >
            <p style={p({ color: '#99a1af', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 })}>Notes</p>
            <p style={p({ color: '#4a5565', fontSize: 14, marginTop: 4, lineHeight: 1.5, whiteSpace: 'pre-wrap' })}>{payload.notes}</p>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function waitForImages(root: HTMLElement): Promise<void> {
  const imgs = [...root.querySelectorAll('img')];
  return Promise.all(
    imgs.map(
      (img) =>
        new Promise<void>((resolve) => {
          if (img.complete && img.naturalWidth > 0) {
            resolve();
            return;
          }
          const done = () => resolve();
          img.addEventListener('load', done, { once: true });
          img.addEventListener('error', done, { once: true });
        })
    )
  ).then(() => undefined);
}

function canvasToPdfBlob(canvas: HTMLCanvasElement): Blob {
  const imgData = canvas.toDataURL('image/png', 1.0);
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pdfWidth = doc.internal.pageSize.getWidth();
  const pdfHeight = doc.internal.pageSize.getHeight();
  const imgWidth = pdfWidth;
  const imgHeight = (canvas.height * pdfWidth) / canvas.width;
  let heightLeft = imgHeight;
  let position = 0;

  doc.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
  heightLeft -= pdfHeight;

  while (heightLeft > 0) {
    position = heightLeft - imgHeight;
    doc.addPage();
    doc.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pdfHeight;
  }

  return doc.output('blob');
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error || new Error('Could not read image blob'));
    reader.readAsDataURL(blob);
  });
}

async function embedImageUrlAsDataUrl(url?: string | null): Promise<string | null> {
  const src = url?.trim();
  if (!src || src.startsWith('data:') || src.startsWith('blob:')) {
    return src || null;
  }

  try {
    const response = await fetch(src, { mode: 'cors' });
    if (!response.ok) {
      return src;
    }

    const blob = await response.blob();
    if (blob.type && !blob.type.startsWith('image/')) {
      return src;
    }

    return await blobToDataUrl(blob);
  } catch {
    return src;
  }
}

async function prepareInvoicePdfPayload(payload: InvoicePdfPayload): Promise<InvoicePdfPayload> {
  const clinicLogoUrl = await embedImageUrlAsDataUrl(payload.clinicLogoUrl);
  if (clinicLogoUrl === payload.clinicLogoUrl) {
    return payload;
  }
  return { ...payload, clinicLogoUrl };
}

async function renderInvoiceToCanvas(payload: InvoicePdfPayload): Promise<HTMLCanvasElement> {
  const host = document.createElement('div');
  host.setAttribute('aria-hidden', 'true');
  host.style.cssText = [
    'position:fixed',
    'left:-12000px',
    'top:0',
    `width:${INVOICE_CARD_PX}px`,
    'opacity:0',
    'pointer-events:none',
    'z-index:-1',
  ].join(';');
  document.body.appendChild(host);

  const mount = document.createElement('div');
  host.appendChild(mount);

  const root = createRoot(mount);
  root.render(<InvoiceHtmlView payload={payload} />);

  await new Promise<void>((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });

  const el = mount.querySelector('[data-invoice-pdf-root]') as HTMLElement | null;
  if (!el) {
    root.unmount();
    document.body.removeChild(host);
    throw new Error('Invoice PDF: render root not found');
  }

  if (document.fonts?.ready) {
    try {
      await document.fonts.ready;
    } catch {
      /* ignore */
    }
  }

  await waitForImages(el);

  try {
    const canvas = await html2canvas(el, {
      scale: 2,
      useCORS: true,
      allowTaint: false,
      backgroundColor: '#ffffff',
      logging: false,
      width: el.scrollWidth,
      height: el.scrollHeight,
    });
    return canvas;
  } finally {
    root.unmount();
    document.body.removeChild(host);
  }
}

function buildFileName(invoiceNo: string): string {
  const safe = (invoiceNo || 'invoice').replace(/[^\w-]+/g, '_');
  const stamp = new Date().toISOString().slice(0, 10);
  return `${safe}_${stamp}.pdf`;
}

export async function generateInvoicePdfBlob(payload: InvoicePdfPayload): Promise<Blob> {
  const preparedPayload = await prepareInvoicePdfPayload(payload);
  const canvas = await renderInvoiceToCanvas(preparedPayload);
  return canvasToPdfBlob(canvas);
}

export async function downloadInvoicePdf(payload: InvoicePdfPayload): Promise<void> {
  const blob = await generateInvoicePdfBlob(payload);
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = buildFileName(payload.invoiceNo);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);
}

/**
 * Opens the same invoice PDF in a hidden iframe and triggers the browser print dialog.
 * Uses the same document as download — matches on-screen invoice details.
 */
export async function printInvoicePdf(payload: InvoicePdfPayload): Promise<void> {
  const blob = await generateInvoicePdfBlob(payload);
  const objectUrl = window.URL.createObjectURL(blob);

  const iframe = document.createElement('iframe');
  iframe.setAttribute('title', 'Invoice print');
  iframe.setAttribute(
    'style',
    'position:fixed;right:0;bottom:0;width:0;height:0;border:0;opacity:0;pointer-events:none'
  );
  iframe.src = objectUrl;
  document.body.appendChild(iframe);

  iframe.onload = () => {
    window.setTimeout(() => {
      const tryIframePrint = () => {
        const win = iframe.contentWindow;
        if (!win) return false;
        win.focus();
        win.print();
        return true;
      };

      try {
        if (!tryIframePrint()) {
          throw new Error('No print context');
        }
      } catch (err) {
        console.warn('Invoice iframe print failed, trying new tab', err);
        const fallback = window.open(objectUrl, '_blank', 'noopener,noreferrer');
        if (fallback) {
          fallback.addEventListener('load', () => {
            window.setTimeout(() => {
              try {
                fallback.print();
              } catch {
                /* user can print from the PDF viewer */
              }
            }, 400);
          });
        } else {
          throw new Error('Print blocked: allow pop-ups or use Download instead.');
        }
      }

      window.setTimeout(() => {
        if (iframe.parentNode) {
          iframe.parentNode.removeChild(iframe);
        }
      }, 500);

      window.setTimeout(() => {
        window.URL.revokeObjectURL(objectUrl);
      }, 120000);
    }, 200);
  };
}
